import React from 'react';
import './LegalPage.css';

const sections = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'information-we-collect', title: 'Information We Collect' },
  { id: 'mobile-app-play-store', title: 'Apple & Google Play Data Safety' },
  { id: 'how-we-use-information', title: 'How We Use Your Information' },
  { id: 'legal-basis', title: 'Legal Basis for Processing' },
  { id: 'cookies', title: 'Cookies & Tracking Technologies' },
  { id: 'sharing', title: 'How We Share Information' },
  { id: 'data-retention', title: 'Data Retention' },
  { id: 'data-security', title: 'Data Security' },
  { id: 'your-rights', title: 'Your Privacy Rights' },
  { id: 'childrens-privacy', title: "Children's Privacy" },
  { id: 'international-transfers', title: 'International Data Transfers' },
  { id: 'third-party-links', title: 'Third-Party Links & Services' },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' },
];

export default function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <section className="page-hero">
        <div className="container page-hero-content">
          <span className="section-label section-label--blue">Legal</span>
          <h1>Privacy <span style={{ color: '#d5772d' }}>Policy</span></h1>
          <p>How ThirdEye Cybersecurity collects, uses, and protects your information across our website, learning portal, and mobile applications.</p>
          <p className="legal-page-updated">Last updated: September 19, 2026</p>
        </div>
      </section>

      <section className="legal-section">
        <div className="container legal-layout">
          <nav className="legal-toc" aria-label="Table of contents">
            <h4>On This Page</h4>
            <ol>
              {sections.map(s => (
                <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
              ))}
            </ol>
          </nav>

          <div className="legal-content">
            <section id="introduction">
              <h2><span className="legal-num">1.</span> Introduction</h2>
              <p>
                ThirdEye Cybersecurity ("ThirdEye," "we," "us," or "our") respects your privacy and is
                committed to protecting the personal information of everyone who interacts with us -
                whether you visit our website, enroll in a training program, engage our staff
                augmentation or IT services, apply for a job, or use any mobile application we publish,
                including on the Google Play Store and Apple App Store (collectively, the "Services").
              </p>
              <p>
                This Privacy Policy explains what information we collect, how we use and share it, and
                the choices and rights available to you. By accessing or using our Services, you agree
                to the practices described in this Policy. If you do not agree, please do not use the
                Services.
              </p>
            </section>

            <section id="information-we-collect">
              <h2><span className="legal-num">2.</span> Information We Collect</h2>
              <p>We collect the following categories of information:</p>
              <h3>a) Information you provide directly</h3>
              <ul>
                <li>Contact details such as name, email address, phone number, and company name when you submit a contact, training enrollment, staffing, or job application form.</li>
                <li>Resumes, cover letters, and other documents you upload when applying for a role.</li>
                <li>Account credentials and profile information when you register on our Learning Portal or mobile application.</li>
                <li>Payment and billing details when you purchase training or services (processed via our payment or invoicing partners; we do not store full card numbers).</li>
                <li>Messages, attachments, and other content you send us through contact forms or support channels.</li>
              </ul>
              <h3>b) Information collected automatically</h3>
              <ul>
                <li>Log and usage data, such as IP address, browser type, pages viewed, referring URLs, and timestamps.</li>
                <li>Device information, including device model, operating system and version, unique device identifiers, and mobile network information when you use our mobile application.</li>
                <li>Approximate location derived from IP address or, with your permission, precise device location.</li>
                <li>Cookies and similar tracking technologies, as described in Section 6.</li>
              </ul>
              <h3>c) Information from third parties</h3>
              <ul>
                <li>Information from recruitment platforms, professional networks, or referral partners when relevant to a job or staffing engagement.</li>
                <li>Information from analytics, advertising, and infrastructure providers that help us operate the Services.</li>
              </ul>
            </section>

            <section id="mobile-app-play-store">
              <h2><span className="legal-num">3.</span> Apple & Google Play Data Safety</h2>
              <p>
                Where ThirdEye offers a mobile application (including any application distributed via
                the Google Play Store or Apple App Store), the following additional practices apply, in
                line with Google Play's Data Safety requirements and Apple's App Store privacy
                guidelines:
              </p>
              <ul>
                <li><strong>Data collected:</strong> account information (name, email), device identifiers, app usage and diagnostic data (crash logs, performance data), and, only if you grant permission, camera, storage, or location access needed for specific in-app features.</li>
                <li><strong>Permissions:</strong> the app requests only the device permissions necessary for its functionality (for example, storage access to download course materials, or notifications to alert you about training schedules). You can review and revoke permissions at any time from your device settings.</li>
                <li><strong>Data sharing:</strong> we do not sell your personal data. Limited data may be shared with service providers (such as analytics, crash-reporting, and cloud-hosting vendors) strictly to operate and improve the app, and with Google or Apple as required to distribute the app through their stores.</li>
                <li><strong>Data encryption:</strong> data transmitted between the app and our servers is encrypted in transit; data at rest is stored using industry-standard security controls described in Section 9.</li>
                <li><strong>Data deletion:</strong> you may request deletion of your in-app account and associated data at any time by contacting us at the address in Section 15, or by using the in-app account deletion option where available.</li>
                <li><strong>Ads:</strong> our applications do not display third-party advertising unless expressly stated within the app.</li>
              </ul>
              <p>
                If you install our app from the Google Play Store, your use is also subject to the{' '}
                <a className="legal-link" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Play Terms of Service and Privacy Policy</a>, and if installed from the Apple App
                Store, to <a className="legal-link" href="https://www.apple.com/legal/internet-services/itunes/" target="_blank" rel="noreferrer">Apple's Media Services Terms and Conditions</a>, in addition to this Policy.
              </p>
            </section>

            <section id="how-we-use-information">
              <h2><span className="legal-num">4.</span> How We Use Your Information</h2>
              <ul>
                <li>To provide, operate, and maintain our website, Learning Portal, and mobile application.</li>
                <li>To process training enrollments, staffing requests, service inquiries, and job applications.</li>
                <li>To communicate with you, including responding to inquiries and sending service, security, and administrative notices.</li>
                <li>To send marketing communications and newsletters where you have opted in, with the ability to unsubscribe at any time.</li>
                <li>To personalize content, improve our Services, and develop new offerings.</li>
                <li>To monitor, detect, and prevent fraud, abuse, and security incidents.</li>
                <li>To comply with legal obligations and enforce our Terms of Service.</li>
              </ul>
            </section>

            <section id="legal-basis">
              <h2><span className="legal-num">5.</span> Legal Basis for Processing</h2>
              <p>
                Where applicable data protection law (such as the EU/UK GDPR) requires it, we rely on the
                following legal bases to process personal data: your consent (for example, for marketing
                emails or optional app permissions), performance of a contract (for example, to deliver
                training or staffing services you request), our legitimate interests (such as improving
                and securing the Services), and compliance with legal obligations.
              </p>
            </section>

            <section id="cookies">
              <h2><span className="legal-num">6.</span> Cookies & Tracking Technologies</h2>
              <p>
                We use cookies, web beacons, and similar technologies to operate our website, remember
                your preferences, analyze traffic, and measure the effectiveness of our communications.
                You can control cookies through your browser settings; disabling cookies may affect the
                functionality of parts of the Services. Our mobile application may use analogous
                technologies such as mobile analytics SDKs and device identifiers for similar purposes.
              </p>
            </section>

            <section id="sharing">
              <h2><span className="legal-num">7.</span> How We Share Information</h2>
              <p>We do not sell your personal information. We may share information with:</p>
              <ul>
                <li><strong>Service providers</strong> who perform functions on our behalf, such as email delivery, hosting, analytics, and payment processing, under contractual confidentiality and data protection obligations.</li>
                <li><strong>Business partners</strong> involved in fulfilling a training, staffing, or IT services engagement you have requested.</li>
                <li><strong>Legal and regulatory authorities</strong> where required to comply with applicable law, regulation, legal process, or governmental request.</li>
                <li><strong>Successors</strong> in connection with a merger, acquisition, financing, or sale of assets, subject to standard confidentiality arrangements.</li>
              </ul>
            </section>

            <section id="data-retention">
              <h2><span className="legal-num">8.</span> Data Retention</h2>
              <p>
                We retain personal information for as long as necessary to fulfil the purposes described
                in this Policy, including to provide the Services, comply with legal, tax, and accounting
                obligations, resolve disputes, and enforce our agreements. When information is no longer
                needed, we securely delete or anonymize it.
              </p>
            </section>

            <section id="data-security">
              <h2><span className="legal-num">9.</span> Data Security</h2>
              <p>
                We implement administrative, technical, and physical safeguards designed to protect
                personal information from unauthorized access, disclosure, alteration, or destruction,
                including encryption in transit, access controls, and regular security reviews. However,
                no method of transmission or storage is completely secure, and we cannot guarantee
                absolute security.
              </p>
            </section>

            <section id="your-rights">
              <h2><span className="legal-num">10.</span> Your Privacy Rights</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul>
                <li>Access the personal information we hold about you and request a copy of it.</li>
                <li>Correct inaccurate or incomplete personal information.</li>
                <li>Request deletion of your personal information, subject to legal exceptions.</li>
                <li>Object to or restrict certain processing, including marketing communications.</li>
                <li>Request portability of information you provided to us.</li>
                <li>Withdraw consent at any time where processing is based on consent, without affecting processing carried out before withdrawal.</li>
              </ul>
              <p>
                To exercise any of these rights, contact us using the details in Section 15. We will
                respond within the timeframe required by applicable law.
              </p>
            </section>

            <section id="childrens-privacy">
              <h2><span className="legal-num">11.</span> Children's Privacy</h2>
              <p>
                Our Services are intended for professionals, job applicants, and organizations, and are
                not directed to children under the age of 16. We do not knowingly collect personal
                information from children. If we become aware that we have inadvertently collected
                personal information from a child, we will take steps to delete it promptly. If you
                believe a child has provided us with personal information, please contact us.
              </p>
            </section>

            <section id="international-transfers">
              <h2><span className="legal-num">12.</span> International Data Transfers</h2>
              <p>
                We are based in India and may process and store information in India and other countries
                where our service providers operate. Where we transfer personal information across
                borders, we take steps to ensure it receives an adequate level of protection consistent
                with this Policy and applicable law.
              </p>
            </section>

            <section id="third-party-links">
              <h2><span className="legal-num">13.</span> Third-Party Links & Services</h2>
              <p>
                Our Services may contain links to third-party websites, social media platforms, or our
                Learning Portal hosted at a separate domain. We are not responsible for the privacy
                practices or content of third-party sites. We encourage you to review the privacy
                policies of any third-party service you access.
              </p>
            </section>

            <section id="changes">
              <h2><span className="legal-num">14.</span> Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices
                or for legal, operational, or regulatory reasons. We will post the revised Policy on this
                page with an updated "Last updated" date. Material changes will be communicated through
                the website, app, or by email where appropriate.
              </p>
            </section>

            <section id="contact">
              <h2><span className="legal-num">15.</span> Contact Us</h2>
              <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or our data
                practices, please contact us at:
              </p>
              <div className="legal-contact-box">
                <strong>Shiddhipriya IT Consulting INC DBA ThirdEye Cyber Security</strong>
                4868 Wright Road,<br />
                Regina, S4W 0A8, Canada<br />
                Email: <a className="legal-link" href="mailto:info@thirdeyecybersecurity.com">info@thirdeyecybersecurity.com</a>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}
